import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Presentation, Star } from "lucide-react";
import dayjs from "dayjs";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import { DEMO_REPORT_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Stat, inputCls } from "../../components/assignment/shared";
import { offsetLabel } from "../../components/email/DemoReminderCard";

const OUTCOME = {
    attended:   ["Attended", "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"],
    no_show:    ["No show", "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"],
    no_outcome: ["Not updated", "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"],
    upcoming:   ["Upcoming", "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"],
    moved:      ["Moved", "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"],
    other:      ["Other", "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"],
};
const SENT_DOT = { sent: "bg-green-500", failed: "bg-red-500", skipped: "bg-gray-300", pending: "bg-blue-300", sending: "bg-yellow-400" };
const pctText = (v) => (v === null || v === undefined ? "—" : `${v}%`);

/**
 * Reports -> Demo Report. Reminders: every booked demo time in the period,
 * the reminders sent, whether the lead confirmed or moved it, and what
 * happened. Feedback: the after-demo feedback requests and answers.
 * email/demo-report/ (platform_core/email_integration/demo_reminder_views.py).
 */
export default function DemoReport() {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [range, setRange] = useState({
        from: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
        to: dayjs().add(7, "day").format("YYYY-MM-DD"),
    });
    const [tab, setTab] = useState("reminders");
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        setData(null);
        fetch(DEMO_REPORT_URL, range).then(res => {
            if (res?.status === "success") { setData(res.data); setError(""); }
            else setError(res?.message || "Could not load the report");
        });
    }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

    const openLead = (id) => navigate(`/customers/customerdetails?id=${id}`, { state: { formEdit: true, row: { id } } });
    const LeadLink = ({ id, name }) => (
        <button type="button" onClick={() => openLead(id)} className="text-brand-600 dark:text-brand-300 hover:underline text-left">
            {name || `Lead #${id}`}
        </button>
    );

    return (
        <ContainerCard>
            <div className="p-4 space-y-5 overflow-auto">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Presentation className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Demo Report</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Reminders before each demo, who confirmed or moved it, who turned up — and the feedback after.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <input type="date" value={range.from} aria-label="From"
                            onChange={e => setRange(r => ({ ...r, from: e.target.value }))} className={cn(inputCls, "w-36")} />
                        <input type="date" value={range.to} aria-label="To"
                            onChange={e => setRange(r => ({ ...r, to: e.target.value }))} className={cn(inputCls, "w-36")} />
                    </div>
                </div>

                <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700" role="tablist">
                    {[["reminders", "Reminders"], ["feedback", "Feedback"]].map(([key, label]) => (
                        <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                            className={cn("px-3 py-2 text-sm font-medium border-b-2 -mb-px",
                                tab === key ? "border-primary text-primary" : "border-transparent text-gray-500 dark:text-gray-400")}>
                            {label}
                        </button>
                    ))}
                </div>

                {error && <p className="text-xs text-gray-500">{error}</p>}
                {!data && !error && <p className="text-xs text-gray-400">Loading…</p>}

                {data && tab === "reminders" && <Reminders data={data.reminders} LeadLink={LeadLink} />}
                {data && tab === "feedback" && <Feedback data={data.feedback} LeadLink={LeadLink} />}
            </div>
        </ContainerCard>
    );
}

function Reminders({ data, LeadLink }) {
    const s = data.summary;
    return (
        <>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                <Stat label="Demos booked" value={s.demos} hint={`${s.upcoming} still to come`} />
                <Stat label="Reminded" value={s.reminded} hint={s.failed ? `${s.failed} reminder(s) failed` : "At least one reminder sent"}
                    tone={s.failed ? "warn" : undefined} />
                <Stat label="Confirmed" value={s.confirmed} hint={`${s.no_reply} reminded, no reply`} />
                <Stat label="Moved by the lead" value={s.rescheduled} />
                <Stat label="Attended" value={pctText(s.attended_rate)} hint={`${s.attended} attended · ${s.no_show} no show`} />
                <Stat label="Not updated" value={s.no_outcome} tone={s.no_outcome ? "warn" : undefined}
                    hint="Demo time passed, still Demo Fixed" />
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-xs text-gray-600 dark:text-gray-300">
                Turned up: <b>{pctText(s.attended_rate_confirmed)}</b> of leads who confirmed vs{" "}
                <b>{pctText(s.attended_rate_unconfirmed)}</b> of those who didn't.
            </div>
            {data.rows.length === 0 ? (
                <p className="text-xs text-gray-400">No demos booked in this period.</p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                            <tr>{["Lead", "Demo", "Presenter", "Owner", "Reminders", "Lead's answer", "Outcome"].map(c =>
                                <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {data.rows.map(r => {
                                const [label, cls] = OUTCOME[r.outcome] || OUTCOME.other;
                                return (
                                    <tr key={`${r.customer_id}-${r.demo_at}`}>
                                        <td className="px-3 py-2"><LeadLink id={r.customer_id} name={r.customer_name} /></td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-200">{r.demo_label}</td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.presenter || "—"}</td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.owner || "Unassigned"}</td>
                                        <td className="px-3 py-2">
                                            {r.reminders.length ? (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {r.reminders.map(x => (
                                                        <span key={`${x.channel}-${x.offset_minutes}`} className="inline-flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300"
                                                            title={`${x.channel === "whatsapp" ? "WhatsApp" : "Email"} · ${offsetLabel(x.offset_minutes)} · ${x.status}${x.reason ? ` — ${x.reason}` : ""}`}>
                                                            <span className={cn("w-2 h-2 rounded-full", SENT_DOT[x.status] || "bg-gray-300")} />
                                                            {x.channel === "whatsapp" ? "WA" : "Email"} {offsetLabel(x.offset_minutes).replace(" before", "")}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            {r.response === "confirmed" && <span className="text-green-700 dark:text-green-300 font-medium">Confirmed</span>}
                                            {r.response === "rescheduled" && <span className="text-amber-700 dark:text-amber-300 font-medium">Moved to {r.new_demo_label}</span>}
                                            {!r.response && <span className="text-gray-400">—</span>}
                                            {r.note && <span className="block text-[10px] text-gray-500 italic max-w-[16rem] truncate" title={r.note}>“{r.note}”</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium", cls)}>{label}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

function Feedback({ data, LeadLink }) {
    const s = data.summary;
    const ch = s.by_channel || {};
    return (
        <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Stat label="Asked" value={s.asked} hint={`Email ${ch.email?.sent || 0} · WhatsApp ${ch.whatsapp?.sent || 0}`} />
                <Stat label="Opened the link" value={s.opened} />
                <Stat label="Answered" value={s.responses} hint={`${pctText(s.response_rate)} of those asked`} />
                <Stat label="Average rating" value={s.avg_rating ? `${s.avg_rating} / 5` : "—"} />
                <Stat label="Low ratings (2★ or less)" value={s.low_ratings} tone={s.low_ratings ? "warn" : undefined} />
            </div>
            <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">By presenter</h3>
                {data.presenters.length === 0 ? <p className="text-xs text-gray-400">No feedback in this period.</p> : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                                <tr>{["Presenter", "Answers", "Demo rating", "Presenter rating"].map(c =>
                                    <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {data.presenters.map(p => (
                                    <tr key={p.presenter}>
                                        <td className="px-3 py-2 text-gray-800 dark:text-gray-100">{p.presenter}</td>
                                        <td className="px-3 py-2 tabular-nums">{p.responses}</td>
                                        <td className="px-3 py-2 tabular-nums">{p.avg_rating}</td>
                                        <td className="px-3 py-2 tabular-nums">{p.avg_presenter_rating ?? "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
            <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Answers</h3>
                {data.rows.length === 0 ? <p className="text-xs text-gray-400">No feedback in this period.</p> : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                                <tr>{["Lead", "When", "Rating", "Presenter", "Fit", "Next step", "Concerns", "Comments"].map(c =>
                                    <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {data.rows.map(r => (
                                    <tr key={`${r.customer_id}-${r.submitted_at}`}>
                                        <td className="px-3 py-2"><LeadLink id={r.customer_id} name={r.customer_name} /></td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500">{dayjs(r.submitted_at).format("D MMM, h:mm A")}</td>
                                        <td className="px-3 py-2">
                                            <span className={cn("inline-flex items-center gap-0.5 font-medium", r.rating <= 2 ? "text-red-600" : "text-amber-600 dark:text-amber-400")}>
                                                <Star className="h-3 w-3 fill-current" aria-hidden="true" /> {r.rating}/5
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                                            {r.presenter || "—"}{r.presenter_rating ? ` (${r.presenter_rating}/5)` : ""}
                                        </td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.fit || "—"}</td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.next_step || "—"}</td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{r.concerns.join(", ") || "—"}</td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200 max-w-[18rem]">{r.comments || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </>
    );
}
